import React from 'react';
import { 
  Box, Stack, Typography, Chip, 
  alpha, useTheme, 
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Checkbox
} from '@mui/material';

const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];
const ACCOUNT_TYPES = ['Checking', 'Savings', 'Business', 'Credit Card'];
const STATES = [
  'NATIONWIDE',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const EditBankModal = ({ open, onClose, bank, onEdit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const [formData, setFormData] = React.useState({
    name: '',
    bonusAmount: 0,
    directDepositAmount: 0,
    timeframe: 0,
    requirements: '',
    states: [],
    difficulty: 'Medium',
    status: 'pending',
    successRate: 0,
    accountType: 'Checking',
    description: '',
    terms: '',
    url: '',
    expiration: '',
    hardPull: false,
    chexSystems: false
  });

  React.useEffect(() => {
    if (bank) {
      console.log('EditBankModal - Bank data received:', bank);
      console.log('EditBankModal - Bank URL field:', bank.url);

      // Convert expiration to YYYY-MM-DD format for date input
      let expirationDate = '';
      if (bank.expiration) {
        try {
          // If it's already in YYYY-MM-DD format, use it directly
          if (typeof bank.expiration === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bank.expiration)) {
            expirationDate = bank.expiration;
          } else {
            // Parse the date and format it without timezone conversion
            const date = new Date(bank.expiration);
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            expirationDate = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Error parsing expiration date:', e);
        }
      }

      setFormData({
        name: bank.name || '',
        bonusAmount: bank.bonusAmount || 0,
        directDepositAmount: bank.directDepositAmount || 0,
        timeframe: bank.timeframe || 0,
        requirements: Array.isArray(bank.requirements) ? bank.requirements.join('\n') : bank.requirements || '',
        states: bank.states || [],
        difficulty: bank.difficulty || 'Medium',
        status: bank.status || 'pending',
        successRate: bank.successRate || 0,
        accountType: bank.accountType || 'Checking',
        description: bank.description || '',
        terms: bank.terms || '',
        url: bank.url || '',
        expiration: expirationDate,
        hardPull: bank.hardPull || false,
        chexSystems: bank.chexSystems || false
      });
    }
  }, [bank]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    const updatedBank = {
      ...formData,
      bonusAmount: Number(formData.bonusAmount),
      directDepositAmount: Number(formData.directDepositAmount),
      timeframe: Number(formData.timeframe),
      successRate: Number(formData.successRate),
      requirements: formData.requirements.split('\n').filter(req => req.trim()),
      expiration: formData.expiration || null
    };
    onEdit(updatedBank);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? alpha('#1E293B', 0.98) : '#FFFFFF',
          backgroundImage: 'none',
          borderRadius: 2,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 600,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2
      }}>
        {bank ? 'Edit Bank Bonus' : 'Add Bank Bonus'}
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
            Basic Information
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Bank Name"
              value={formData.name}
              onChange={handleChange('name')}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={formData.accountType}
                onChange={handleChange('accountType')}
                label="Account Type"
              >
                {ACCOUNT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Bonus Details */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mt: 2 }}>
            Bonus Details
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Bonus Amount"
              type="number"
              value={formData.bonusAmount}
              onChange={handleChange('bonusAmount')}
              fullWidth
              required
              InputProps={{
                startAdornment: <Typography>$</Typography>
              }}
            />
            <TextField
              label="Direct Deposit Amount"
              type="number"
              value={formData.directDepositAmount}
              onChange={handleChange('directDepositAmount')}
              fullWidth
              InputProps={{
                startAdornment: <Typography>$</Typography>
              }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Timeframe (Days)"
              type="number"
              value={formData.timeframe}
              onChange={handleChange('timeframe')}
              fullWidth
            />
            <TextField
              label="Success Rate"
              type="number"
              value={formData.successRate}
              onChange={handleChange('successRate')}
              fullWidth
              InputProps={{
                endAdornment: <Typography>%</Typography>,
                inputProps: { min: 0, max: 100 }
              }}
            />
          </Stack>

          {/* Requirements and Terms */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mt: 2 }}>
            Requirements and Terms
          </Typography>
          <TextField
            label="Requirements"
            multiline
            rows={4}
            value={formData.requirements}
            onChange={handleChange('requirements')}
            fullWidth
            helperText="Enter each requirement on a new line"
          />
          <TextField
            label="Terms & Conditions"
            multiline
            rows={3}
            value={formData.terms}
            onChange={handleChange('terms')}
            fullWidth
          />

          {/* Additional Details */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mt: 2 }}>
            Additional Details
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={formData.difficulty}
                onChange={handleChange('difficulty')}
                label="Difficulty"
              >
                {DIFFICULTY_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>States Available</InputLabel>
              <Select
                multiple
                value={formData.states}
                onChange={handleChange('states')}
                label="States Available"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={value}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                {STATES.map((state) => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Offer URL"
              value={formData.url}
              onChange={handleChange('url')}
              fullWidth
            />
            <TextField
              label="Expiration Date"
              type="date"
              value={formData.expiration}
              onChange={handleChange('expiration')}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Credit Check
              </Typography>
              <Stack direction="row" spacing={2}>
                <Checkbox
                  checked={formData.hardPull}
                  onChange={handleChange('hardPull')}
                />
                <Typography variant="body2">
                  Hard Pull Required
                </Typography>
              </Stack>
            </FormControl>
            <FormControl fullWidth>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ChexSystems
              </Typography>
              <Stack direction="row" spacing={2}>
                <Checkbox
                  checked={formData.chexSystems}
                  onChange={handleChange('chexSystems')}
                />
                <Typography variant="body2">
                  ChexSystems Sensitive
                </Typography>
              </Stack>
            </FormControl>
          </Stack>

          <TextField
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${theme.palette.divider}`,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 100
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 100
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBankModal; 